import { useCallback, useEffect, useMemo, useState } from "react";
import { MapObject, MapWithGeofence } from "~/features/map-view/ui/map-view";
import { MapFilter } from "~/features/map-view/ui/map-filter";
import { MapSearch } from "~/features/map-view/ui/map-search";
import { SiteMarkersLayer } from "~/features/map-view/components/site-markers";
import { buildSitePins } from "~/shared/lib/pickup-status/site-pins";
import { useJobLabel } from "~/shared/lib/pickup-status/use-job-label";
import { setDashboardDay, useDashboardDay } from "~/shared/lib/pickup-status/dashboard-day";
import RoutesSchedulePage, { type MapTruckPick } from "~/components/routes";
import { DashboardFilters, usePickupStatus } from "~/shared/lib/pickup-status/use-pickup-status";
import { useListPickupLocationsQuery } from "~/shared/api/pickup-locations/pickup-locations.grpc";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useAllTruckStatusesQuery,
  useLazyGetTruckListLatestPositionsQuery,
} from "~/shared/api/truck/truck.grpc";
import { useInfoPanel } from "~/features/info-panel/model/useInfoPanel";
import { DriverOnShiftStatus, ConnectProto, enumToJson } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { useMapLocations } from "~/shared/api/locations/use-map-locations";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useDelayedFlag } from "~/shared/hooks/use-delayed-flag";
import { Loader2 } from "lucide-react";
import { IcCancel } from "@racoon/icons";

// Remounted per company, so another company's dashboard never queries the previous one's
// division.
export const DashboardPage = () => {
  const { activeCompanyId } = useAuthSession();
  return <CompanyDashboard key={activeCompanyId ?? "none"} />;
};

const CompanyDashboard = () => {
  const t = useT();
  const { open, infoType, infoId } = useInfoPanel();
  const { activeCompanyId, isReceiverUser } = useAuthSession();

  // The day lives in the shared store, which the side panel reads too.
  const day = useDashboardDay();
  const [scoped, setScoped] = useState<Omit<DashboardFilters, "day">>({
    category: "all",
    divisionId: null,
  });
  const filters = useMemo<DashboardFilters>(() => ({ day, ...scoped }), [day, scoped]);
  const updateFilters = useCallback(({ day: nextDay, ...patch }: Partial<DashboardFilters>) => {
    if (nextDay) setDashboardDay(nextDay);
    if (Object.keys(patch).length > 0) setScoped((prev) => ({ ...prev, ...patch }));
  }, []);

  // Filter state - use arrays for multiple selection; no bucket selected shows them all.
  const [jobFilters, setJobFilters] = useState<string[]>([]);
  const [truckFilters, setTruckFilters] = useState<string[]>(["with-shift"]);
  const [schedulePick, setSchedulePick] = useState<MapTruckPick | null>(null);

  const { jobs: bucketedJobs, isFetching: jobsFetching } = usePickupStatus({
    ...filters,
    organizationId: activeCompanyId ?? null,
  });

  const jobsLoading = useDelayedFlag(jobsFetching);

  const sitePins = useMemo(
    () =>
      buildSitePins(
        bucketedJobs.filter(({ bucket }) => jobFilters.length === 0 || jobFilters.includes(bucket)),
      ),
    [bucketedJobs, jobFilters],
  );
  // An open route is shown alone, whatever the job filters hide.
  const shownSitePins = useMemo(
    () =>
      infoType === "route"
        ? buildSitePins(bucketedJobs.filter(({ job }) => String(job.routeId) === infoId))
        : sitePins,
    [infoType, infoId, bucketedJobs, sitePins],
  );

  const jobLabel = useJobLabel();

  // Every site of the org is a big payload, needed only to search or pin an opened site.
  const [pickupsWanted, setPickupsWanted] = useState(false);
  const wantPickups = useCallback(() => setPickupsWanted(true), []);
  // An opened site with no pin that day needs the list to be drawn; one with a pin doesn't.
  useEffect(() => {
    if (infoType !== "pickup" || jobsFetching) return;
    if (!sitePins.some((site) => site.id === infoId)) setPickupsWanted(true);
  }, [infoType, infoId, jobsFetching, sitePins]);

  const { currentData: pickupLocationsResponse } = useListPickupLocationsQuery(
    activeCompanyId && pickupsWanted
      ? {
          serviceProviderIds: activeCompanyId ? [BigInt(activeCompanyId)] : [],
        }
      : skipToken,
  );

  const pickupLocations = useMemo(() => {
    return (
      pickupLocationsResponse?.pickupLocations.filter(
        (item) => item.status === ConnectProto.LocationStatus.ACTIVE,
      ) || []
    );
  }, [pickupLocationsResponse]);

  const { data: trucksList } = useAllTruckStatusesQuery(
    activeCompanyId ? { ownerOrganizationIds: [BigInt(activeCompanyId)] } : skipToken,
  );

  const [getLatestTruckStatus, { data: truckStatus }] = useLazyGetTruckListLatestPositionsQuery();

  const { data: depotsResponse } = useMapLocations();

  const depotsData = useMemo(
    () =>
      (depotsResponse?.locations ?? []).filter(
        (location) => location.status === ConnectProto.LocationStatus.ACTIVE,
      ),
    [depotsResponse],
  );

  const depots: MapObject[] = useMemo(() => {
    if (!depotsData) return [];
    return depotsData.map((depot, index) => ({
      type: "depot",
      name: depot.name,
      locationType: depot.type,
      // int64 depot IDs can exceed Number.MAX_SAFE_INTEGER — keep as string.
      id: String(depot.id),
      lat: depot.lat,
      lon: depot.lon,
      localIndex: index + 1,
    })) as unknown as MapObject[];
  }, [depotsData]);

  // Pickup locations stay searchable; on the map they gave way to job markers.
  const objects: MapObject[] = useMemo(() => {
    if (!pickupLocations) return [];
    return pickupLocations.map((location, index) => ({
      type: "pickup",
      name: location.name,
      id: location.id,
      lat: location.lat,
      lon: location.lon,
      containerGroups: (location.containerGroups ?? [])
        .filter((group) => Number(group.containerType?.ownerOrganizationId) === activeCompanyId)
        .filter(
          (group, groupIndex, groups) =>
            groups.findIndex((g) => g.ownerOrganization?.id === group.ownerOrganization?.id) ===
            groupIndex,
        ),
      localIndex: index + 1,
      geometry: location.geometry,
    })) as unknown as MapObject[];
  }, [pickupLocations, activeCompanyId]);

  // A site opened from search keeps a pin, or there would be nothing to zoom to; one with
  // jobs that day already has its own.
  const selectedObjects = useMemo(
    () =>
      infoType === "pickup" && !sitePins.some((site) => site.id === infoId)
        ? objects.filter((o) => String(o.id) === infoId)
        : [],
    [objects, infoType, infoId, sitePins],
  );

  // A selected site drawn by the site layer isn't in `objects`, so the map flies to it here.
  const focusPoint = useMemo(() => {
    const site = infoType === "pickup" ? sitePins.find((s) => s.id === infoId) : undefined;
    return site ? { lat: site.lat, lon: site.lon, key: site.id } : null;
  }, [infoType, infoId, sitePins]);

  const trucks: MapObject[] = useMemo(() => {
    const positions = truckStatus?.truckPositions ?? {};
    const statuses = trucksList?.truckStatuses ?? [];
    return Object.entries(positions).map(([truckId, position]): MapObject => {
      const truckData = statuses.find((s) => String(s.truck?.id) === truckId);
      const currentShift = truckData?.currentShift;
      const currentRoute = truckData?.currentRoute;
      const hasOrders = (currentRoute?.jobs?.length ?? 0) > 0;
      const hasShift = currentShift != null;
      return {
        type: "truck",
        name: truckData?.truck?.plateNumber ?? "",
        // Keep the map key as a string: int64 truck IDs can exceed
        // Number.MAX_SAFE_INTEGER.
        id: truckId,
        lat: position.latitude,
        lon: position.longitude,
        angle: position.angle,
        route: currentRoute,
        // enumToJson returns the full proto JSON name (e.g.
        // "DRIVER_ON_SHIFT_STATUS_ENROUTE"), consistent with the ts-proto
        // string-enum fallback below — no extra prefixing needed.
        status:
          currentShift?.status != null
            ? enumToJson(ConnectProto.DriverOnShiftStatusSchema, Number(currentShift.status))
            : DriverOnShiftStatus.DRIVER_ON_SHIFT_STATUS_TRUCK_IDLE,
        truckType:
          truckData?.truck?.type != null
            ? enumToJson(ConnectProto.TruckTypeSchema, Number(truckData.truck.type))
            : undefined,
        hasOrders: hasOrders,
        hasShift: hasShift,
        ownerOrganizationId: truckData?.truck?.ownerOrganizationId,
      };
    });
  }, [truckStatus, trucksList]);

  // Filtered trucks
  const filteredTrucks = useMemo(() => {
    // A truck the dispatcher arrived here to see — a plate link elsewhere puts
    // its id in the URL — stays on the map whatever the filters say. Hiding it
    // would defeat the link, and with no marker there is nothing to zoom to.
    const selectedTruckId = infoType === "truck" ? infoId : null;

    return trucks.filter((truck) => {
      if (selectedTruckId && String(truck.id) === selectedTruckId) return true;
      // An open route keeps its truck: map-view already narrows to it under focusRoute.
      if (schedulePick && infoType !== "route" && !schedulePick.truckIds.has(String(truck.id)))
        return false;

      // If no filters are selected, show all
      if (truckFilters.length === 0) return true;

      // Check each active filter
      for (const filter of truckFilters) {
        if (filter === "with-orders" && truck.hasOrders) {
          return true;
        }
        if (filter === "with-shift" && truck.hasShift) {
          return true;
        }
      }

      return false;
    });
  }, [trucks, truckFilters, schedulePick, infoType, infoId]);

  useEffect(() => {
    if (!activeCompanyId) return;
    const fetchLatest = () =>
      getLatestTruckStatus({ ownerOrganizationIds: [BigInt(activeCompanyId)] });

    fetchLatest();
    const statusInterval = setInterval(fetchLatest, 10000);

    return () => {
      clearInterval(statusInterval);
    };
  }, [activeCompanyId, getLatestTruckStatus]);

  const openSite = useCallback((id: string) => open("pickup", id), [open]);

  if (!activeCompanyId) return null;

  return (
    <div className="space-y-4 w-full flex flex-1 flex-col">
      <div className="rounded-xl border bg-white overflow-hidden flex flex-1 relative">
        {/* Filters */}
        <div className="absolute top-2 left-2 z-10 flex flex-wrap items-center gap-2">
          <MapFilter
            jobFilters={jobFilters}
            truckFilters={truckFilters}
            onJobFiltersChange={setJobFilters}
            onTruckFiltersChange={setTruckFilters}
          />
          {schedulePick && (
            <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-blue-600 pl-3.5 pr-1.5 text-sm font-medium text-white shadow-sm">
              <span className="max-w-[40vw] truncate">{schedulePick.label}</span>
              <button
                type="button"
                onClick={schedulePick.clear}
                title={t(Namespace.Common, "reset")}
                aria-label={`${t(Namespace.Common, "reset")}: ${schedulePick.label}`}
                className="flex h-5 w-5 items-center justify-center rounded-full hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <IcCancel aria-hidden className="h-5 w-5" />
              </button>
            </span>
          )}
        </div>

        {/* Search */}
        <div className="absolute top-2 right-14 z-[10]">
          <MapSearch
            objects={objects}
            trucks={trucks}
            onActivate={wantPickups}
            onSelect={(item: MapObject) => {
              open(item.type, String(item.id));
              // Reset filters so found object is displayed
              setJobFilters([]);
              setTruckFilters([]);
            }}
          />
        </div>

        {jobsLoading && (
          <div
            role="status"
            className="pointer-events-none absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-sm text-text-primary shadow-md"
          >
            <Loader2 className="h-4 w-4 animate-spin text-brand-blue motion-reduce:animate-none" />
            {t(Namespace.Routes, "loadingJobs")}
          </div>
        )}

        <MapWithGeofence
          showPlayer
          focusRoute
          fitPoints={sitePins}
          clusterPickups
          showPickupIndex={false}
          depots={depots}
          objects={selectedObjects}
          trucks={filteredTrucks}
          focusPoint={focusPoint}
          onMarkerClick={function (item: MapObject): void {
            open(item.type, String(item.id));
          }}
        >
          <SiteMarkersLayer
            sites={shownSitePins}
            selectedId={infoType === "pickup" ? infoId : null}
            onSiteClick={openSite}
            jobLabel={jobLabel}
            moreLabel={t(Namespace.Infopanel, "siteJobs.more")}
            dimmed={jobsLoading}
          />
        </MapWithGeofence>
      </div>
      {!isReceiverUser && (
        <RoutesSchedulePage
          filters={filters}
          onFiltersChange={updateFilters}
          jobsLoading={jobsFetching}
          onMapTrucks={setSchedulePick}
        />
      )}
    </div>
  );
};
